import { useEffect, useState } from 'react';
import { listOrganizations } from '../../api/organizations';
import { useOrganizationStore } from '../../stores/organizationStore';

interface Props {
  value: string | null;
  onChange: (orgId: string | null) => void;
  disabled?: boolean;
}

const selectStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: 13,
  width: '100%',
};

export function OrgSelector({ value, onChange, disabled }: Props) {
  const organizations = useOrganizationStore((s) => s.organizations);
  const setOrganizations = useOrganizationStore((s) => s.setOrganizations);

  useEffect(() => {
    if (organizations.length === 0) {
      listOrganizations('flat').then(setOrganizations).catch(() => {});
    }
  }, [organizations.length, setOrganizations]);

  const cantons = organizations.filter((o) => o.level === 'canton');

  // Derive canton and commune from the current value
  const selectedOrg = organizations.find((o) => o.id === value);
  const derivedCantonId =
    selectedOrg?.level === 'commune'
      ? selectedOrg.parent_id
      : selectedOrg?.level === 'canton'
        ? selectedOrg.id
        : null;
  const derivedCommuneId = selectedOrg?.level === 'commune' ? selectedOrg.id : null;

  const [cantonId, setCantonId] = useState<string | null>(derivedCantonId);

  // Sync internal canton state when value changes externally
  useEffect(() => {
    setCantonId(derivedCantonId);
  }, [derivedCantonId]);

  const communes = cantonId
    ? organizations.filter((o) => o.level === 'commune' && o.parent_id === cantonId)
    : [];

  function handleCantonChange(newCantonId: string | null) {
    setCantonId(newCantonId);
    // When canton changes, reset to canton-level (no commune selected)
    onChange(newCantonId);
  }

  function handleCommuneChange(communeId: string | null) {
    if (communeId) {
      onChange(communeId);
    } else {
      // "No commune" selected → fall back to canton
      onChange(cantonId);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <select
        style={selectStyle}
        value={cantonId ?? ''}
        onChange={(e) => handleCantonChange(e.target.value || null)}
        disabled={disabled}
      >
        <option value="">-- Select Canton --</option>
        {cantons.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} ({c.code})
          </option>
        ))}
      </select>

      {cantonId && communes.length > 0 && (
        <select
          style={selectStyle}
          value={derivedCommuneId ?? ''}
          onChange={(e) => handleCommuneChange(e.target.value || null)}
          disabled={disabled}
        >
          <option value="">-- Canton level (no commune) --</option>
          {communes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.code})
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
